import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Select, Radio, Button, Card, Row, Col, message, Tooltip, Upload } from 'antd';
import { QuestionCircleOutlined, UploadOutlined } from '@ant-design/icons';
import PersianDatePicker from '../PersianDatePicker';
import { scrollToFirstError } from '../../utils/formHelpers';
import { handleFormSubmitError } from '../../utils/errorHandler';
import { departmentsApi, designationsApi, rolesApi, employeesApi, companyAddressesApi } from '../../utils/api';

const { Option } = Select;
const { TextArea } = Input;

const EMPLOYEE_FIELD_MAP = {
  department_id: 'department_id',
  designation_id: 'designation_id',
  'employee_detail.department.id': 'department_id',
  'employee_detail.designation.id': 'designation_id',
  'employee_detail.joining_date': 'joining_date',
  joining_date: 'joining_date',
  date_of_birth: 'date_of_birth',
  reporting_to: 'reporting_to',
  company_address_id: 'company_address',
  employee_detail: 'employee_detail',
  image: 'image',
  role_id: 'role_ids',
  role: 'role_ids',
  role_ids: 'role_ids',
};

const EmployeeForm = ({ 
  initialValues = null, 
  onSave, 
  onSaveAndAddMore, 
  onCancel, 
  loading = false,
  isEdit = false,
  existingImageUrl = null,
}) => {
  const [form] = Form.useForm();
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [reportingEmployees, setReportingEmployees] = useState([]);
  const [companyAddresses, setCompanyAddresses] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [deleteImage, setDeleteImage] = useState(false);

  // بارگذاری داده‌ها از API
  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [deptRes, desRes, rolesRes, reportRes, addrRes] = await Promise.all([
          departmentsApi.getAll(),
          designationsApi.getAll(),
          rolesApi.getAll(),
          employeesApi.getAll(),
          companyAddressesApi.getAll(),
        ]);
        
        setDepartments(deptRes.data || []);
        setDesignations(desRes.data || []);
        const rolesData = Array.isArray(rolesRes) ? rolesRes : (rolesRes?.data?.roles || rolesRes?.data || []);
        setRoles(rolesData);
        setReportingEmployees((reportRes.data || []).map((u) => ({ id: u.id, name: u.name })) || []);
        const addrData = Array.isArray(addrRes) ? addrRes : (addrRes?.data?.company_addresses || addrRes?.data || []);
        setCompanyAddresses(addrData);
      } catch (error) {
        console.error('خطا در دریافت گزینه‌ها:', error);
        message.error('خطا در دریافت اطلاعات');
      } finally {
        setLoadingOptions(false);
      }
    };
    
    loadOptions();
  }, []);

  // تنظیم مقادیر اولیه فرم
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    } else if (!isEdit) {
      form.setFieldsValue({
        gender: 'male',
        locale: 'fa',
        login: 'yes',
        email_notifications: 'no',
        marital_status: null,
        role_ids: [],
      });
    }
  }, [initialValues, form, isEdit]);

  useEffect(() => {
    setImageFile(null);
    setDeleteImage(false);
  }, [existingImageUrl, isEdit]);

  const handleSubmit = async (values) => {
    if (onSave) {
      await onSave(values, { imageFile, deleteImage });
    }
  };

  const handleFormFinish = async (values) => {
    try {
      await handleSubmit(values);
    } catch (error) {
      handleFormSubmitError(form, error, 'خطا در ذخیره کارمند', { fieldMap: EMPLOYEE_FIELD_MAP });
    }
  };

  const handleSaveAndAddMore = async () => {
    try {
      const values = await form.validateFields();
      if (onSaveAndAddMore) {
        await onSaveAndAddMore(values, { imageFile, deleteImage });
        form.resetFields();
        setImageFile(null);
        setDeleteImage(false);
        form.setFieldsValue({
          gender: 'male',
          locale: 'fa',
          login: 'yes',
          email_notifications: 'no',
          role_ids: [],
        });
      }
    } catch (error) {
      handleFormSubmitError(form, error, 'خطا در ذخیره کارمند', { fieldMap: EMPLOYEE_FIELD_MAP });
    }
  };

  // تبدیل تاریخ از فرمت فارسی به میلادی
  const formatDateForApi = (dateValue) => {
    if (!dateValue) return null;
    try {
      // اگر Date object است
      if (dateValue instanceof Date) {
        return dateValue.toISOString().slice(0, 10);
      }
      // اگر string است و فرمت YYYY/MM/DD دارد (تاریخ شمسی)
      if (typeof dateValue === 'string' && dateValue.includes('/')) {
        const [year, month, day] = dateValue.split('/');
        // تبدیل تاریخ شمسی به میلادی (ساده‌سازی - در پروژه واقعی باید از کتابخانه تبدیل استفاده شود)
        // برای حالا فرض می‌کنیم که PersianDatePicker خودش Date object برمی‌گرداند
        return null;
      }
      // اگر string با فرمت ISO است
      if (typeof dateValue === 'string' && dateValue.includes('-')) {
        return dateValue.slice(0, 10);
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFormFinish}
      onFinishFailed={() => scrollToFirstError(form)}
      scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
      className="space-y-6"
      style={{ fontFamily: 'CustomFont' }}
    >
      {/* بخش جزئیات حساب */}
      <Card 
        title={<span style={{ fontFamily: 'CustomFont' }}>جزئیات حساب</span>}
        className="mb-6"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <span>
                  شناسه کارمند{' '}
                  <Tooltip title={<span>شناسه منحصربه‌فردی که برای شناسایی کارمندان در سازمان خود استفاده می‌کنید.<br/>برای کارمندانی که از طریق <b>دعوت‌نامه</b> اضافه می‌شوند، به‌صورت خودکار شناسه ایجاد می‌شود که بعداً می‌توانید آن را تغییر دهید.</span>}>
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="employee_id"
              rules={[{ required: true, message: 'لطفاً شناسه کارمند را وارد کنید' }]}
            >
              <Input placeholder="مثلاً: 100ca" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>
          </Col>

          {!isEdit && (
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="کلمه عبور"
                name="password"
                rules={[{ required: true, message: 'لطفاً کلمه عبور را وارد کنید' }]}
              >
                <Input.Password placeholder="مثلاً: 123456" style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>
          )}

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="نام کارمند"
              name="name"
              rules={[{ required: true, message: 'لطفاً نام کارمند را وارد کنید' }]}
            >
              <Input placeholder="مثلاً: کارمند تست" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="ایمیل کارمند"
              name="email"
              rules={isEdit ? [] : [
                { required: true, message: 'لطفاً ایمیل را وارد کنید' },
                { type: 'email', message: 'ایمیل معتبر نیست' },
              ]}
            >
              <Input placeholder="مثلاً: test@email.com" style={{ fontFamily: 'CustomFont' }} disabled={isEdit} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="موبایل"
              name="mobile"
              rules={isEdit ? [] : [{ required: true, message: 'لطفاً موبایل را وارد کنید' }]}
            >
              <Input placeholder="مثلاً: 09121212121" style={{ fontFamily: 'CustomFont' }} disabled={isEdit} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="دپارتمان"
              name="department_id"
              rules={[{ required: true, message: 'لطفاً دپارتمان را انتخاب کنید' }]}
            >
              <Select
                placeholder="انتخاب دپارتمان"
                style={{ fontFamily: 'CustomFont' }}
                loading={loadingOptions}
                optionFilterProp="children"
                showSearch
                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {departments.map((d) => (
                  <Option key={d.id} value={d.id}>
                    {d.team_name || d.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="سمت شغلی"
              name="designation_id"
              rules={[{ required: true, message: 'لطفاً سمت شغلی را انتخاب کنید' }]}
            >
              <Select
                placeholder="انتخاب سمت شغلی"
                style={{ fontFamily: 'CustomFont' }}
                loading={loadingOptions}
                optionFilterProp="children"
                showSearch
                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {designations.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="نقش‌های کاربری"
              name="role_ids"
              extra="نقش «کارمند» همیشه فعال است. برای شرکت‌های کوچک می‌توان چند نقش را به یک نفر داد؛ دسترسی‌ها ترکیب می‌شوند."
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="فقط کارمند (پیش‌فرض)"
                style={{ fontFamily: 'CustomFont' }}
                loading={loadingOptions}
                optionFilterProp="children"
                filterOption={(input, option) => String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {roles
                  .filter((r) => r.name !== 'client' && r.name !== 'employee')
                  .map((r) => (
                    <Option key={r.id} value={r.id}>
                      {r.display_name || r.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="تاریخ پیوستن"
              name="joining_date"
              rules={[{ required: true, message: 'لطفاً تاریخ پیوستن را انتخاب کنید' }]}
            >
              <PersianDatePicker
                placeholder="تاریخ را انتخاب کنید"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="تاریخ تولد"
              name="date_of_birth"
            >
              <PersianDatePicker
                placeholder="تاریخ را انتخاب کنید"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="کدملی"
              name="national_id"
            >
              <Input style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="نام پدر"
              name="father_name"
            >
              <Input placeholder="مثلاً: تست پدر" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="کد بیمه"
              name="insurance_code"
            >
              <Input placeholder="مثلاً: 123456789" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="جنسیت"
              name="gender"
            >
              <Select style={{ fontFamily: 'CustomFont' }}>
                <Option value="male">مرد</Option>
                <Option value="female">زن</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="گزارش دهنده به"
              name="reporting_to"
            >
              <Select
                placeholder="انتخاب مدیر بالادست"
                style={{ fontFamily: 'CustomFont' }}
                loading={loadingOptions}
                showSearch
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {reportingEmployees.map((u) => (
                  <Option key={u.id} value={u.id}>
                    {u.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="زبان"
              name="locale"
            >
              <Select style={{ fontFamily: 'CustomFont' }}>
                <Option value="fa">فارسی</Option>
                <Option value="en">انگلیسی</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label="نشانی"
              name="address"
            >
              <TextArea
                rows={3}
                placeholder="مثلاً: خیابان ولی عصر"
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label="درباره"
              name="about_me"
            >
              <TextArea
                rows={4}
                placeholder="توضیحات..."
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* بخش سایر اطلاعات */}
      <Card 
        title={<span style={{ fontFamily: 'CustomFont' }}>سایر اطلاعات</span>}
        className="mb-6"
      >
        <Row gutter={[16, 16]}>
          {isEdit && (
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="وضعیت کاربر"
                name="status"
              >
                <Select placeholder="انتخاب وضعیت" style={{ fontFamily: 'CustomFont' }}>
                  <Option value="active">فعال</Option>
                  <Option value="deactive">غیرفعال</Option>
                </Select>
              </Form.Item>
            </Col>
          )}

          <Col xs={24} sm={12}>
            <Form.Item
              label="اجازه ورود؟"
              name="login"
            >
              <Radio.Group>
                <Radio value="yes">بله</Radio>
                <Radio value="no">خیر</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label="دریافت اعلان‌های ایمیلی؟"
              name="email_notifications"
            >
              <Radio.Group>
                <Radio value="yes">بله</Radio>
                <Radio value="no">خیر</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="نرخ ساعتی"
              name="hourly_rate"
            >
                <InputNumber
                className="w-full"
                placeholder="مثلاً: ۲۵۰,۰۰۰"
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/,/g, '')}
                style={{ fontFamily: 'CustomFont' }}
                />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="نوع استخدام"
              name="employment_type"
            >
              <Select placeholder="--" style={{ fontFamily: 'CustomFont' }} allowClear>
                <Option value="full_time">تمام وقت</Option>
                <Option value="part_time">پاره وقت</Option>
                <Option value="contract">قراردادی</Option>
                <Option value="intern">کارآموزی</Option>
                <Option value="training">آموزشی</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <span>
                  پایان دوره آزمایشی{' '}
                  <Tooltip title="تاریخ پایان دوره آزمایشی را وارد کنید؛ پس از این تاریخ، کارمند به صورت دائم در شرکت باقی خواهد ماند.">
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="probation_end_date"
            >
              <PersianDatePicker
                placeholder="تاریخ را انتخاب کنید"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <span>
                  شروع دوره اعلام خروج{' '}
                  <Tooltip title="تاریخی که از آن زمان فرایند اطلاع‌رسانی برای خروج کارمند آغاز می‌شود.">
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="notice_period_start_date"
            >
              <PersianDatePicker
                placeholder="تاریخ را انتخاب کنید"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <span>
                  پایان دوره اعلام خروج{' '}
                  <Tooltip title="تاریخی که دوره اطلاع‌رسانی پایان می‌یابد و خروج کارمند نهایی می‌شود. اگر این تاریخ در آینده باشد، کارمند در همان تاریخ به‌صورت خودکار غیرفعال خواهد شد. در صورتی که تاریخ خروج مشخص نشده باشد، این مقدار به‌طور خودکار ثبت می‌شود.">
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="notice_period_end_date"
            >
              <PersianDatePicker
                placeholder="تاریخ را انتخاب کنید"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="تاریخ پایان کارآموزی"
              name="internship_end_date"
            >
              <PersianDatePicker
                placeholder="تاریخ را انتخاب کنید"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="تاریخ پایان قرارداد"
              name="contract_end_date"
            >
              <PersianDatePicker
                placeholder="تاریخ را انتخاب کنید"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="وضعیت تأهل"
              name="marital_status"
            >
              <Select style={{ fontFamily: 'CustomFont' }} allowClear>
                <Option value="single">مجرد</Option>
                <Option value="married">متأهل</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="نشانی کسب و کار"
              name="company_address"
            >
              <Select
                placeholder="انتخاب آدرس شرکت"
                allowClear
                loading={loadingOptions}
                style={{ fontFamily: 'CustomFont' }}
              >
                {companyAddresses.map((addr) => (
                  <Option key={addr.id} value={addr.id}>
                    {addr.location || addr.name || `آدرس ${addr.id}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card
        title={<span style={{ fontFamily: 'CustomFont' }}>تصویر پروفایل</span>}
        className="mb-6"
      >
        <Form.Item name="image" className="mb-0">
          <div className="space-y-3">
            {isEdit && existingImageUrl && !deleteImage && !imageFile ? (
              <div className="inline-flex flex-col items-start gap-2 p-3 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800/40">
                <img
                  src={existingImageUrl}
                  alt="employee"
                  className="h-20 w-20 rounded-full object-cover border"
                />
                <Button
                  danger
                  size="small"
                  onClick={() => {
                    setDeleteImage(true);
                    setImageFile(null);
                  }}
                >
                  حذف تصویر فعلی
                </Button>
              </div>
            ) : null}
            <Upload
              maxCount={1}
              beforeUpload={(file) => {
                setImageFile(file);
                setDeleteImage(false);
                return false;
              }}
              onRemove={() => {
                setImageFile(null);
                return true;
              }}
              fileList={imageFile ? [{ uid: '-emp-image', name: imageFile.name, status: 'done' }] : []}
            >
              <Button icon={<UploadOutlined />}>انتخاب تصویر</Button>
            </Upload>
          </div>
        </Form.Item>
      </Card>

      {/* دکمه‌های عملیات */}
      <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button
          onClick={onCancel}
          size="middle"
          style={{ fontFamily: 'CustomFont' }}
        >
          لغو
        </Button>
        {onSaveAndAddMore && (
          <Button
            onClick={handleSaveAndAddMore}
            size="middle"
            loading={loading}
            style={{ fontFamily: 'CustomFont' }}
          >
            ذخیره و افزودن بیشتر
          </Button>
        )}
        <Button
          type="primary"
          htmlType="submit"
          size="middle"
          loading={loading}
          className="bg-green-600 hover:bg-green-700 border-green-600"
          style={{ fontFamily: 'CustomFont' }}
        >
          ذخیره
        </Button>
      </div>
    </Form>
  );
};

export default EmployeeForm;
